import { discoverPostTopics } from './discovery';

export const TRENDING_DAYS = 7;
export type TrendingTopic = { tag: string; posts: number; creators: number; title?: string; detectedAt?: string };
export type TrendingUpdate = { tag: string; title: string; posts: number; detectedAt: string; latestAt: string };
export type TrendingFeed = { topics: TrendingTopic[]; today?: TrendingUpdate[]; updatedAt: string; windowDays: number; viewerId?: string | null };
export type TrendPost = { id: string; authorId: string; createdAt: string; tags: string[]; body?: string; concepts?: string[]; title?: string; visibility: string; deleted?: boolean; archived?: boolean; authorPrivate?: boolean; blocked?: boolean; likes?: number; comments?: number };

export function shortTrendTitle(value: string, words = 12, characters = 110) {
  const text = value.replace(/\s+/g, ' ').trim(), list = text.split(' ').filter(Boolean);
  let title = list.slice(0, words).join(' ');
  if (title.length > characters) { title = title.slice(0, characters).replace(/\s+\S*$/, '').trim() || text.slice(0, characters); }
  return title + (title.length < text.length ? '…' : '');
}

export function trendingSidebarFeed(posts: TrendPost[], now: number, observed: ReadonlyMap<string, string> = new Map()): TrendingFeed {
  const eligible = posts.filter(post => eligibleTrendPost(post, now, TRENDING_DAYS)), ranked = rankTrendingTopics(eligible, now);
  const terms = new Map(eligible.map(post => [post.id, new Set(discoverPostTopics(post).map(topic => topic.tag))]));
  const day = new Date(now).toISOString().slice(0, 10);
  const headlines = new Set<string>();
  const topics = ranked.flatMap(topic => {
    const candidates = eligible.filter(post => terms.get(post.id)?.has(topic.tag));
    candidates.sort((a, b) => (Number(b.likes || 0) + Number(b.comments || 0)) - (Number(a.likes || 0) + Number(a.comments || 0)) || b.createdAt.localeCompare(a.createdAt));
    const title = candidates.find(post => post.title?.trim())?.title?.trim() || topic.tag.replace(/_/g, ' ');
    const key = title.toLowerCase().replace(/\s+/g, ' ');
    if (headlines.has(key)) return [];
    headlines.add(key);
    return [{ ...topic, title, detectedAt: observed.get(topic.tag) || new Date(now).toISOString() }];
  });
  const today = topics.flatMap(topic => {
    const current = eligible.filter(post => post.createdAt.slice(0, 10) === day && terms.get(post.id)?.has(topic.tag));
    if (!current.length) return [];
    const unique = new Map(current.map(post => [post.id, post]));
    return [{ tag: topic.tag, title: topic.title!, posts: unique.size, detectedAt: topic.detectedAt!, latestAt: [...unique.values()].map(post => post.createdAt).sort().at(-1)! }];
  }).slice(0, 4);
  return { topics, today, updatedAt: new Date(now).toISOString(), windowDays: TRENDING_DAYS };
}

/** Recent public conversations, decayed by age and balanced across creators. */
function eligibleTrendPost(post: TrendPost, now: number, windowDays: number | null) {
  const ageHours = (now - Date.parse(post.createdAt)) / 3600000;
  return post.visibility === 'public' && !post.deleted && !post.archived && !post.authorPrivate && !post.blocked && Number.isFinite(ageHours) && ageHours >= 0 && (windowDays === null || ageHours <= windowDays * 24);
}

export function rankTrendingTopics(posts: TrendPost[], now: number, options: { windowDays?: number | null; limit?: number } = {}): TrendingTopic[] {
  const topics = new Map<string, { posts: Set<string>; authors: Map<string, number[]> }>();
  const seen = new Set<string>();
  for (const post of posts) {
    const ageHours = (now - Date.parse(post.createdAt)) / 3600000;
    if (seen.has(post.id) || !eligibleTrendPost(post, now, options.windowDays === undefined ? TRENDING_DAYS : options.windowDays)) continue;
    seen.add(post.id);
    const engagement = Math.max(0, Number(post.likes) || 0) + Math.max(0, Number(post.comments) || 0);
    const weight = Math.pow(.5, Math.min(ageHours, 365 * 24) / 24) * (1 + Math.min(1.5, Math.log1p(engagement) * .15));
    for (const { tag, weight: relevance } of discoverPostTopics(post)) {
      const topic = topics.get(tag) || { posts: new Set<string>(), authors: new Map<string, number[]>() };
      topic.posts.add(post.id);
      topic.authors.set(post.authorId, [...(topic.authors.get(post.authorId) || []), weight * relevance].sort((a, b) => b - a).slice(0, 3));
      topics.set(tag, topic);
    }
  }
  const ranked = [...topics].map(([tag, topic]) => ({ tag, ids: topic.posts, posts: topic.posts.size, creators: topic.authors.size, score: [...topic.authors.values()].flat().reduce((sum, weight) => sum + weight, 0) * (1 + Math.log2(topic.authors.size)) }))
    .sort((a, b) => b.score - a.score || b.creators - a.creators || a.tag.localeCompare(b.tag));
  const selected: typeof ranked = [];
  for (const topic of ranked) {
    // A caption's overlapping words/phrases must not crowd out other conversations.
    if (selected.some(other => { const overlap = [...topic.ids].filter(id => other.ids.has(id)).length; return overlap / topic.ids.size >= .9 && overlap / other.ids.size >= .9; })) continue;
    selected.push(topic);
    if (selected.length >= (options.limit || 6)) break;
  }
  return selected.map(({ tag, posts, creators }) => ({ tag, posts, creators }));
}
